// file: src/pages/OrdersPage.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Badge,
  Button,
  Spinner,
  Text,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Switch,
  HStack,
  useToast,
  IconButton,
  Input,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardBody,
  CardHeader,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Checkbox,
  FormControl,
  FormLabel,
  Select,
} from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { CopyIcon, InfoOutlineIcon, CheckIcon, CloseIcon, QuestionIcon, EmailIcon } from "@chakra-ui/icons";

import { useCognito } from "../utils/useCognito";
import { formatDateTime } from "../utils/dateUtils";

import UpdateShippingModal from "../components/UpdateShippingModal";
import CreateInvoiceModal from "../components/CreateInvoiceModal";
import ViewInvoiceModal from "../components/ViewInvoiceModal";
import ViewPdfModal from "../components/ViewPdfModal";
import PackingListInputModal from "../components/PackingListInputModal";
import EmailOrderPdfModal from "../components/EmailOrderPdfModal";
import OrderActionsMenu from "../components/OrderActionsMenu";
import OrderBulkActionBar from "../components/OrderBulkActionBar";

import { getProductByQCode, getProductListClean, syncInvoiceWithOrder } from "../utils/commonApiCalls";

// 🔌 API endpoints
const ORDERS_API = "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getAllMedicalOrders";
const USERS_API = "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getUser";
const BAA_API = "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getBaaData";
const COMBINE_PACKING_API = "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/combinePackingLists";

/* ---------------------------
   Ultra-compact table styles
---------------------------- */
const COMPACT_TABLE_SX = {
  tableLayout: "fixed",
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: "0 6px",
  "th, td": {
    px: 3,
    py: 2.5,
    verticalAlign: "middle",
  },
  th: {
    fontSize: "xs",
    letterSpacing: "0.02em",
    color: "gray.600",
    textTransform: "uppercase",
  },
  td: {
    fontSize: "sm",
    lineHeight: "1.35",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
};

const USER_CELL_SUBLINE_PROPS = { fontSize: "xs", color: "gray.600" };

// facility/patient header paddings
const FACILITY_HEADER_P = 3;
const PATIENT_HEADER_P = 2;

/** -------------------------
 * Helpers: facility index
 * ------------------------*/
function buildFacilitiesIndex(enrichedOrders) {
  const byFacility = {};

  for (const order of enrichedOrders) {
    const facility = order.facilityName || "Unknown Facility";

    const patientName = (order.patientName || "Unknown Patient").trim();
    const patientMBI = order.patientMBI || order.mbi || "";
    const patientSSN = order.patientSSN || order.ssn || "";

    const patientKey = `${patientName}||${patientMBI}||${patientSSN}`;

    if (!byFacility[facility]) byFacility[facility] = {};
    if (!byFacility[facility][patientKey]) {
      byFacility[facility][patientKey] = {
        patientName,
        patientMBI,
        patientSSN,
        orders: [],
      };
    }
    byFacility[facility][patientKey].orders.push(order);
  }

  const sorted = {};
  Object.entries(byFacility).forEach(([facility, patientsObj]) => {
    const patientEntries = Object.entries(patientsObj).sort((a, b) =>
      a[1].patientName.localeCompare(b[1].patientName, undefined, { sensitivity: "base" })
    );

    const normalized = patientEntries.map(([patientKey, bucket]) => {
      const ordersSorted = [...bucket.orders].sort((a, b) => {
        const dateA = new Date(a.updatedAt || 0);
        const dateB = new Date(b.updatedAt || 0);
        return dateB - dateA;
      });
      return [patientKey, { ...bucket, orders: ordersSorted }];
    });

    sorted[facility] = Object.fromEntries(normalized);
  });

  return sorted;
}

/** -----------------------------------
 * Wound meta helpers (unchanged)
 * ----------------------------------*/
const firstOf = (obj, keys = []) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
};

const collectWoundish = (obj) => {
  const out = {};
  if (!obj || typeof obj !== "object") return out;

  const take = (o) => {
    for (const [k, v] of Object.entries(o)) {
      const key = String(k).toLowerCase();
      if (v === undefined || v === null || v === "") continue;

      if (key === "woundtype" || key.endsWith("_type")) out.type ??= v;
      if (key === "woundlength" || key.endsWith("length") || key.endsWith("len")) out.length ??= v;
      if (key === "woundwidth" || key.endsWith("width") || key.endsWith("wid")) out.width ??= v;
      if (key === "wounddepth" || key.endsWith("depth") || key.endsWith("dep")) out.depth ??= v;

      if (key === "woundsize" || key.endsWith("area") || key === "cm2" || key === "cm²") out.area ??= v;
      if (key === "woundsizetext" || key.endsWith("sizetext")) out.sizeText ??= v;

      if (key.includes("wound")) {
        if (!out.type && key.includes("type")) out.type = v;
        if (!out.length && (key.includes("length") || key.endsWith("len"))) out.length = v;
        if (!out.width && (key.includes("width") || key.endsWith("wid"))) out.width = v;
        if (!out.depth && (key.includes("depth") || key.endsWith("dep"))) out.depth = v;
        if (!out.sizeText && key.includes("sizetext")) out.sizeText = v;
        if (!out.area && (key.endsWith("size") || key.includes("area"))) out.area = v;
      }
    }
  };

  take(obj);
  for (const v of Object.values(obj)) {
    if (v && typeof v === "object" && !Array.isArray(v)) take(v);
  }
  return out;
};

const getWoundMeta = (order, woundIndex, graftHint = {}) => {
  const idx = typeof woundIndex === "string" ? parseInt(woundIndex, 10) : woundIndex;

  const fromArrayIndex = Array.isArray(order?.wounds) ? order.wounds[idx] : undefined;
  const fromFinder =
    Array.isArray(order?.wounds) &&
    order.wounds.find((w) => (w?.woundIndex ?? w?.index ?? w?.idx ?? w?.wound_number) === idx);

  const sources = [fromArrayIndex, fromFinder, graftHint, order, order?.wound].filter(Boolean);

  const merged = sources.reduce((acc, src) => {
    const found = collectWoundish(src);
    acc.type ??= found.type;
    acc.length ??= found.length;
    acc.width ??= found.width;
    acc.depth ??= found.depth;
    acc.sizeText ??= found.sizeText;
    acc.area ??= found.area;
    return acc;
  }, {});

  return merged;
};

// 🧮 Nice, human-ish dimension text (avoid undefineds)
const formatWoundDimensions = ({ length, width, depth, sizeText, area }) => {
  const hasLen = length !== undefined && length !== null && length !== "";
  const hasWid = width !== undefined && width !== null && width !== "";
  const hasDep = depth !== undefined && depth !== null && depth !== "";

  if (hasLen && hasWid && hasDep) return `Size: ${length} x ${width} x ${depth} cm`;
  if (hasLen && hasWid) return `Size: ${length} x ${width} cm`;
  if (sizeText) return `Size: ${sizeText}`;
  if (area !== undefined && area !== null && area !== "") {
    const n = Number(area);
    const areaText = Number.isFinite(n) ? n.toFixed(2) : area;
    return `Area: ${areaText} cm²`;
  }
  return "Size: —";
};

const buildPatientKeyFromOrder = (o) => {
  const patientName = (o.patientName || "Unknown Patient").trim();
  const patientMBI = o.patientMBI || o.mbi || "";
  const patientSSN = o.patientSSN || o.ssn || "";
  return `${patientName}||${patientMBI}||${patientSSN}`;
};

const OrdersPage = () => {
  const { getIdToken } = useCognito();
  const toast = useToast();

  // DATA + UI STATE
  const [enrichedOrdersByFacility, setEnrichedOrdersByFacility] = useState({});
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [shippingModalOpen, setShippingModalOpen] = useState(false);

  const [searchParams] = useSearchParams();
  const isTestMode = searchParams.get("test") === "true";
  const initialFocusOrderId = (searchParams.get("orderId") || searchParams.get("focusOrderId") || "").trim();

  const TEST_USER_IDS = [
    "auth0|67dca56ce48dfa7a186b2b1f",
    "auth0|67cdaa044a714c1f00addefe",
    "auth0|67ef0012b076d56b64707091",
    "auth0|67eebba8f3ef00b630525864",
    "auth0|68081737a26a556b062b0fc2",
    "auth0|67effe3acc3eee3b74316298",
    "auth0|6813d9f226ee7d3fd62139a3",
    "auth0|6838b99566a9c8dfb930f824",
  ];

  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
  const [invoicePrefillData, setInvoicePrefillData] = useState({});

  const [openSections, setOpenSections] = useState({});
  const [viewInvoiceOpen, setViewInvoiceOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  const [packingPdfUrl, setPackingPdfUrl] = useState(null);
  const [viewPackingModalOpen, setViewPackingModalOpen] = useState(false);
  const [packingInProgress, setPackingInProgress] = useState({});

  const [regeneratePdfModalOpen, setRegeneratePdfModalOpen] = useState(false);
  const [pdfGenerationMode, setPdfGenerationMode] = useState("facility");
  const [pdfRegenerationInProgress, setPdfRegenerationInProgress] = useState(false);
  const [sendDraftOrderEmail, setSendDraftOrderEmail] = useState(false);
  const [emailOrderModalOpen, setEmailOrderModalOpen] = useState(false);

  const [packingInputModalOpen, setPackingInputModalOpen] = useState(false);
  const [pendingPackingOrder, setPendingPackingOrder] = useState(null);
  const [packingSubmissionInProgress, setPackingSubmissionInProgress] = useState(false);

  // Bulk packing workflow state
  const [bulkPackingQueue, setBulkPackingQueue] = useState([]);
  const [isBulkPackingMode, setIsBulkPackingMode] = useState(false);
  const [combineModalOpen, setCombineModalOpen] = useState(false);
  const [sharedTrackingNumber, setSharedTrackingNumber] = useState("");
  const [sharedCarrier, setSharedCarrier] = useState("FedEx");
  const [combineInProgress, setCombineInProgress] = useState(false);

  const [openPatients, setOpenPatients] = useState({});
  const [showHelpBox, setShowHelpBox] = useState(false);

  // 🔎 Search (works like IVR page, includes IVR ID)
  const [searchOrderId, setSearchOrderId] = useState("");

  // focus + highlight
  const focusAnchorRef = useRef(null);
  const orderRowRefs = useRef({});
  const [highlightedOrderId, setHighlightedOrderId] = useState(null);
  const persistHighlight = (orderId) => setHighlightedOrderId(orderId);
  const clearHighlight = () => setHighlightedOrderId(null);

  const highlightRow = (orderId, durationMs = 5000) => {
    setHighlightedOrderId(orderId);
    window.clearTimeout(highlightRow._t);
    highlightRow._t = window.setTimeout(() => setHighlightedOrderId(null), durationMs);
  };

  const formatAddress = (addr) =>
    addr ? `${addr.street}, ${addr.city}, ${addr.state} ${addr.zip}` : "—";

  /* -----------------------
     Bulk selection state
  ------------------------*/
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [requireTrackingForCombine, setRequireTrackingForCombine] = useState(false); // Deprecated - tracking set at combine time

  const toggleOrderSelection = useCallback((orderId) => {
    setSelectedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  }, []);

  const clearOrderSelection = useCallback(() => {
    setSelectedOrderIds(new Set());
  }, []);

  const selectedOrders = useMemo(
    () => allOrders.filter((o) => selectedOrderIds.has(o.orderId)),
    [allOrders, selectedOrderIds]
  );

  const bulkStats = useMemo(() => {
    const totalSelected = selectedOrders.length;
    if (!totalSelected) {
      return {
        totalSelected: 0,
        packingCompleteCount: 0,
        packingMissingCount: 0,
        trackingCompleteCount: 0,
        trackingMissingCount: 0,
        canCombine: false,
      };
    }

    let packingCompleteCount = 0;
    let trackingCompleteCount = 0;
    for (const o of selectedOrders) {
      if (o.packingId) packingCompleteCount++;
      if (o.trackingNumber) trackingCompleteCount++;
    }

    const packingMissingCount = totalSelected - packingCompleteCount;
    const trackingMissingCount = totalSelected - trackingCompleteCount;

    const allHavePacking = packingMissingCount === 0;
    // For combining, we only need packing slips - tracking is set at combine time
    const canCombine = totalSelected > 0 && allHavePacking;

    return {
      totalSelected,
      packingCompleteCount,
      packingMissingCount,
      trackingCompleteCount,
      trackingMissingCount,
      canCombine,
    };
  }, [selectedOrders, requireTrackingForCombine]);

  const isOrderSelected = (orderId) => selectedOrderIds.has(orderId);

  /* -----------------------
     Data load (useCallback)
     ---------------------- */
  const loadData = useCallback(async () => {
    const token = getIdToken();

    const [ordersRes, usersRes, baaRes] = await Promise.all([
      fetch(ORDERS_API, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ test: isTestMode }),
      }),
      fetch(USERS_API, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ test: isTestMode }),
      }),
      fetch(BAA_API, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ test: isTestMode }),
      }),
    ]);

    const ordersResult = await ordersRes.json();
    const usersResult = await usersRes.json();
    const baaResult = await baaRes.json();

    const ordersByUser = ordersResult.ordersByUser || {};
    const filteredOrdersByUser = isTestMode
      ? Object.fromEntries(Object.entries(ordersByUser).filter(([userId]) => TEST_USER_IDS.includes(userId)))
      : Object.fromEntries(Object.entries(ordersByUser).filter(([userId]) => !TEST_USER_IDS.includes(userId)));

    const users = usersResult.users || [];
    const baaData = baaResult.baaData || [];

    const userMap = Object.fromEntries(users.map((u) => [u.UserId || u.userId, u]));
    const baaMap = Object.fromEntries(
      baaData.map((b) => {
        let facilityAddress = b.facilityAddress;
        if (typeof facilityAddress === "string") {
          try {
            facilityAddress = JSON.parse(facilityAddress);
          } catch {
            facilityAddress = {};
          }
        }
        return [b.UserId || b.userId, { ...b, facilityAddress }];
      })
    );

    const enrichedOrders = Object.values(filteredOrdersByUser)
      .flat()
      .map((order) => {
        const user = userMap[order.userId] || {};
        const baa = baaMap[order.userId] || {};
        const facilityName = baa.facilityName || "Unknown Facility";
        const npi = baa.facilityAddress?.npi || baa.npi || baa.facility?.npi || "—";

        return {
          ...order,
          userEmail: user.email || "—",
          userName: `${user.FirstName || ""} ${user.LastName || ""}`.trim() || "—",
          facilityName,
          facilityNpi: npi,
        };
      });

    const facilitiesIndex = buildFacilitiesIndex(enrichedOrders);
    setEnrichedOrdersByFacility(facilitiesIndex);

    const sortedOrders = [...enrichedOrders].sort(
      (a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0)
    );
    setAllOrders(sortedOrders);

    return facilitiesIndex;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getIdToken, isTestMode]);

  // Initial load
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        await loadData();
      } catch (err) {
        console.error("Failed to fetch orders or related data:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [loadData]);

  // Alt+R refresh without losing place
  useEffect(() => {
    const onKey = async (e) => {
      if (e.altKey && (e.key === "r" || e.key === "R")) {
        e.preventDefault();
        await refreshAndRefocus(focusAnchorRef.current);
        toast({ title: "Orders refreshed", status: "success", duration: 1000 });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helpers: open/scroll/find
  const refreshAndRefocus = useCallback(
    async (anchor) => {
      setIsRefreshing(true);
      try {
        const facilitiesIndex = await loadData();
        if (!anchor) return;
        const { facilityName, patientKey, orderId } = anchor;

        setOpenSections((prev) => ({ ...prev, [facilityName]: true }));
        setOpenPatients((prev) => ({
          ...prev,
          [facilityName]: { ...(prev[facilityName] || {}), [patientKey]: true },
        }));

        requestAnimationFrame(() => {
          const el = orderRowRefs.current[orderId] || document.querySelector(`[data-order-id="${orderId}"]`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          persistHighlight(orderId);
        });

        const patientBucket = facilitiesIndex[facilityName]?.[patientKey];
        const freshOrder = patientBucket?.orders?.find((o) => o.orderId === orderId) || null;
        return freshOrder;
      } finally {
        setIsRefreshing(false);
      }
    },
    [loadData]
  );

  const findOrderAnchor = (targetIdRaw) => {
    if (!targetIdRaw) return null;
    const targetId = String(targetIdRaw).trim().toLowerCase();

    for (const [facilityName, patientsObj] of Object.entries(enrichedOrdersByFacility)) {
      for (const [patientKey, bucket] of Object.entries(patientsObj)) {
        const hit = bucket.orders?.find((o) => String(o.orderId).toLowerCase() === targetId);
        if (hit) return { facilityName, patientKey, order: hit };
      }
    }
    return null;
  };

  const openAndScrollToFoundOrder = ({ facilityName, patientKey, order }) => {
    setOpenSections((prev) => ({ ...prev, [facilityName]: true }));
    setOpenPatients((prev) => ({
      ...prev,
      [facilityName]: { ...(prev[facilityName] || {}), [patientKey]: true },
    }));
    requestAnimationFrame(() => {
      const el = orderRowRefs.current[order.orderId] || document.querySelector(`[data-order-id="${order.orderId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      persistHighlight(order.orderId);
    });
  };

  // Deep link jump once data available
  useEffect(() => {
    if (!initialFocusOrderId) return;
    const t = setTimeout(() => {
      const found = findOrderAnchor(initialFocusOrderId);
      if (found) openAndScrollToFoundOrder(found);
    }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFocusOrderId, enrichedOrdersByFacility]);

  const handleJumpToOrder = () => {
    const id = searchOrderId.trim();
    if (!id) {
      toast({ title: "Enter something to search", status: "info", duration: 1200, isClosable: true });
      return;
    }
    // Search by orderId, ivrId, patient name, user name, facility, email
    const lower = id.toLowerCase();

    const direct = findOrderAnchor(id);
    if (direct) {
      openAndScrollToFoundOrder(direct);
      return;
    }

    // fallback: filter list and focus first hit
    const hit =
      allOrders.find(
        (o) =>
          o.orderId.toLowerCase().includes(lower) ||
          (o.ivrId && String(o.ivrId).toLowerCase().includes(lower)) ||
          (o.patientName || "").toLowerCase().includes(lower) ||
          (o.userName || "").toLowerCase().includes(lower) ||
          (o.userEmail || "").toLowerCase().includes(lower) ||
          (o.facilityName || "").toLowerCase().includes(lower)
      ) || null;

    if (!hit) {
      toast({ title: "No results", description: id, status: "warning", duration: 1800, isClosable: true });
      return;
    }

    for (const [facilityName, patientsObj] of Object.entries(enrichedOrdersByFacility)) {
      for (const [patientKey, bucket] of Object.entries(patientsObj)) {
        const found = bucket.orders?.find((o) => o.orderId === hit.orderId);
        if (found) {
          openAndScrollToFoundOrder({ facilityName, patientKey, order: found });
          return;
        }
      }
    }
  };

  /* ------------------
     Quick Stats
  -------------------*/
  const stats = useMemo(() => {
    const total = allOrders.length;
    const inProgress = allOrders.filter((o) =>
      ["in progress", "processing", "draft", "submitted"].includes(String(o.status || "").toLowerCase())
    ).length;
    const completed = allOrders.filter((o) =>
      ["completed", "shipped", "fulfilled"].includes(String(o.status || "").toLowerCase())
    ).length;
    const pendingAction = total - (inProgress + completed);
    return { total, inProgress, completed, pendingAction };
  }, [allOrders]);

  /* ---------------------------
     Chronological table view
  ---------------------------*/
  const filteredChronologicalOrders = useMemo(() => {
    if (!searchOrderId) return allOrders;
    const searchTerm = searchOrderId.toLowerCase();
    return allOrders.filter(
      (order) =>
        order.orderId.toLowerCase().includes(searchTerm) ||
        (order.ivrId && String(order.ivrId).toLowerCase().includes(searchTerm)) ||
        order.patientName?.toLowerCase().includes(searchTerm) ||
        order.userName?.toLowerCase().includes(searchTerm) ||
        order.facilityName?.toLowerCase().includes(searchTerm) ||
        order.userEmail?.toLowerCase().includes(searchTerm)
    );
  }, [allOrders, searchOrderId]);

  const renderChronologicalView = () => {
    const rows = filteredChronologicalOrders;
    if (!rows.length) {
      return (
        <Card>
          <CardBody>
            <VStack spacing={4} py={8}>
              <Text fontSize="lg" color="gray.500">
                No orders match your search.
              </Text>
              <Text fontSize="sm" color="gray.400">
                Try Order ID, IVR ID, patient, facility, user, or email.
              </Text>
            </VStack>
          </CardBody>
        </Card>
      );
    }

    return (
      <Card>
        <CardHeader>
          <Text fontSize="lg" fontWeight="semibold">
            All Orders — Chronological ({rows.length})
          </Text>
        </CardHeader>
        <CardBody>
          <VStack align="stretch" spacing={3}>
            {rows.map((order) =>
              renderOrderRow(order, {
                showFacility: true,
                showShippingAddress: false,
              })
            )}
          </VStack>
        </CardBody>
      </Card>
    );
  };


    /* ---------------------------
     Shared row renderer
  ---------------------------*/
  const renderOrderRow = (
    order,
    {
      showFacility = true,
      showShippingAddress = false,
    } = {}
  ) => {
    const isSelected = isOrderSelected(order.orderId);
    const highlighted = highlightedOrderId === order.orderId;
    const packingLoading = Boolean(packingInProgress[order.orderId]);

    const hasGrafts = Array.isArray(order.graftsUsed) && order.graftsUsed.length > 0;
    const hasIndexedWounds = hasGrafts && order.graftsUsed.some((g) => g.woundIndex !== undefined);

    const woundEntries = (() => {
      if (!hasGrafts) return [];
      if (hasIndexedWounds) {
        const groups = order.graftsUsed.reduce((acc, g) => {
          const idx = g.woundIndex ?? 0;
          if (!acc[idx]) acc[idx] = [];
          acc[idx].push(g);
          return acc;
        }, {});
        return Object.entries(groups).sort(
          ([a], [b]) => parseInt(a, 10) - parseInt(b, 10)
        );
      }
      return [["0", order.graftsUsed]]; // single wound bucket
    })();

    return (
      <Box
        key={order.orderId}
        ref={(el) => {
          if (el) orderRowRefs.current[order.orderId] = el;
        }}
        data-order-id={order.orderId}
        borderWidth="1px"
        borderRadius="lg"
        bg={highlighted ? "blue.50" : "white"}
        p={3}
        mb={3}
        boxShadow="sm"
        _hover={{
          boxShadow: "md",
          bg: highlighted ? "blue.50" : "gray.50",
        }}
      >
        {/* Top row: IDs / patient / status / actions */}
        <Flex direction={["column", "row"]} align="stretch" gap={3}>
          {/* LEFT: selection + core identity */}
          <Flex flex="1" gap={3}>
            <Box pt={1}>
              <Checkbox
                isChecked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleOrderSelection(order.orderId);
                }}
                size="sm"
                colorScheme="teal"
                borderColor="var(--primary-color)"
                _hover={{ borderColor: "var(--primary-color)" }}
                _checked={{
                  bg: "var(--primary-color)",
                  borderColor: "var(--primary-color)",
                  _hover: {
                    bg: "var(--primary-color)",
                    borderColor: "var(--primary-color)",
                  },
                }}
              />
            </Box>

            <Box flex="1">
              {/* Order / IVR IDs */}
              <HStack spacing={2} mb={1} wrap="wrap">
                <HStack spacing={1}>
                  <Text fontWeight="bold" fontSize="sm">
                    {order.orderId}
                  </Text>
                  <Tooltip label="Copy Order ID" hasArrow>
                    <IconButton
                      aria-label="Copy Order ID"
                      size="xs"
                      variant="ghost"
                      icon={<CopyIcon />}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(order.orderId);
                          toast({
                            title: "Order ID copied",
                            status: "success",
                            duration: 1200,
                          });
                        } catch {
                          toast({ title: "Copy failed", status: "error" });
                        }
                      }}
                      _hover={{ transform: "scale(1)" }}
                    />
                  </Tooltip>
                </HStack>

                {order.ivrId && (
                  <HStack spacing={1}>
                    <Badge colorScheme="gray" variant="subtle">
                      IVR
                    </Badge>
                    <Tooltip label={`Copy IVR ID: ${order.ivrId}`} hasArrow>
                      <IconButton
                        aria-label="Copy IVR ID"
                        size="xs"
                        variant="ghost"
                        icon={<InfoOutlineIcon />}
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(order.ivrId);
                            toast({
                              title: "IVR ID copied",
                              description: order.ivrId,
                              status: "success",
                              duration: 1200,
                            });
                          } catch {
                            toast({ title: "Copy failed", status: "error" });
                          }
                        }}
                        _hover={{ transform: "scale(1)" }}
                      />
                    </Tooltip>
                    <Text fontSize="xs" color="gray.600">
                      {order.ivrId}
                    </Text>
                  </HStack>
                )}
              </HStack>

              {/* Patient */}
              <Text fontWeight="semibold" fontSize="sm">
                {order.patientName || "Unknown Patient"}
              </Text>
              <Text {...USER_CELL_SUBLINE_PROPS}>
                {[
                  order.patientMBI && `MBI: ${order.patientMBI}`,
                  order.patientSSN && `SSN: ${order.patientSSN}`,
                ]
                  .filter(Boolean)
                  .join(" • ")}
              </Text>

              {/* Facility (optional in facility view) */}
              {showFacility && (
                <Text {...USER_CELL_SUBLINE_PROPS}>
                  {order.facilityName} • NPI: {order.facilityNpi || "—"}
                </Text>
              )}

              {/* Shipping address (optional) */}
              {showShippingAddress && (
                <Text {...USER_CELL_SUBLINE_PROPS}>
                  {formatAddress(order.shippingAddress)}
                </Text>
              )}

              {/* User who created the order */}
              <Text {...USER_CELL_SUBLINE_PROPS}>
                Created by {order.userName} • {order.userEmail}
              </Text>
            </Box>
          </Flex>

          {/* RIGHT: status / dates / tracking / actions */}
          <Box minW={["100%", "280px"]}>
            <VStack
              align={["flex-start", "flex-end"]}
              spacing={1}
              width="100%"
            >
              <HStack spacing={2}>
                <Badge colorScheme="purple">{order.status}</Badge>
                <Tooltip
                  label={
                    order.paid
                      ? "Paid"
                      : "Not Paid"
                  }
                  hasArrow
                >
                  <Badge
                    colorScheme={order.paid ? "green" : "red"}
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    minW={6}
                  >
                    {order.paid ? (
                      <CheckIcon boxSize={3} />
                    ) : (
                      <CloseIcon boxSize={3} />
                    )}
                  </Badge>
                </Tooltip>
                <Tooltip
                  label={
                    order.submittedToDist
                      ? "Sent to Distributor"
                      : "Not sent to Distributor"
                  }
                  hasArrow
                >
                  <EmailIcon
                    boxSize={4}
                    color={order.submittedToDist ? "green.500" : "gray.400"}
                  />
                </Tooltip>
              </HStack>

              <HStack fontSize="xs" color="gray.600" spacing={3} flexWrap="wrap">
                <Text>Delivery: {order.deliveryDate || "—"}</Text>
                <Text>Updated: {formatDateTime(order.updatedAt)}</Text>
              </HStack>

              <HStack fontSize="xs" color="gray.600" spacing={2} flexWrap="wrap">
                <Text>Tracking:</Text>
                {order.trackingNumber ? (
                  String(order.carrier || "").toLowerCase() === "fedex" ? (
                    <a
                      href={`https://www.fedex.com/fedextrack/?tracknumbers=${order.trackingNumber}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "underline" }}
                    >
                      {order.trackingNumber}
                    </a>
                  ) : (
                    <Text>{order.trackingNumber}</Text>
                  )
                ) : (
                  <Text color="gray.500">—</Text>
                )}
              </HStack>

              <OrderActionsMenu
                order={order}
                packingLoading={packingLoading}
                onViewInvoice={(o) => {
                  setSelectedInvoiceId(o.invoiceId);
                  setSelectedOrder(o);
                  setViewInvoiceOpen(true);
                }}
                onCreateInvoice={(o) => handleCreateInvoiceClick(o)}
                onSyncInvoice={async (o) => {
                  const confirmed = window.confirm(
                    "Sync invoice with the latest order data?"
                  );
                  if (!confirmed) return;
                  try {
                    const token = getIdToken();
                    await syncInvoiceWithOrder(
                      {
                        invoiceId: o.invoiceId,
                        orderId: o.orderId,
                        userId: o.userId,
                      },
                      token
                    );

                    const anchor = {
                      facilityName: o.facilityName,
                      patientKey: buildPatientKeyFromOrder(o),
                      orderId: o.orderId,
                    };
                    const freshOrder = await refreshAndRefocus(anchor);

                    toast({
                      title: "Invoice synced",
                      status: "success",
                      duration: 1500,
                    });

                    if (freshOrder?.invoiceId) {
                      setSelectedInvoiceId(freshOrder.invoiceId);
                      setSelectedOrder(freshOrder);
                      setViewInvoiceOpen(true);
                    }
                  } catch (err) {
                    console.error("Sync failed:", err);
                    toast({ title: "Sync failed", status: "error" });
                  }
                }}
                onUpdateTracking={(o) => {
                  setSelectedOrder(o);
                  setShippingModalOpen(true);
                }}
                onCreatePackingList={(o) => {
                  setPendingPackingOrder(o);
                  setPackingInputModalOpen(true);
                }}
                onEditPackingList={(o) => {
                  setPendingPackingOrder(o);
                  setPackingInputModalOpen(true);
                }}
                onViewPackingList={(o) => handleViewPackingList(o.packingId)}
                onRegeneratePackingList={(o) =>
                  handleGeneratePackingList(o, { mode: "regenerate" })
                }
                onCloneOrder={(o) => {
                  window.location.href = `/admin/create-order/${o.ivrId}/${o.userId}?cloneOrderId=${o.orderId}`;
                }}
                onEditOrder={(o) => {
                  window.location.href = `/admin/create-order/${o.ivrId}/${o.userId}?editOrderId=${o.orderId}`;
                }}
                onViewPdf={(o) => {
                  if (o.orderPdfUrl) window.open(o.orderPdfUrl, "_blank");
                }}
                onRegenerateOrderPdf={(o) => {
                  setSelectedOrder(o);
                  setPdfGenerationMode("facility");
                  setRegeneratePdfModalOpen(true);
                }}
                onEmailOrderPdf={(o) => handleEmailOrderPdf(o)}
              />
            </VStack>
          </Box>
        </Flex>

        {/* Bottom: graft / wound summary */}
        <Box mt={3} pt={3} borderTopWidth="1px">
          {hasGrafts ? (
            hasIndexedWounds ? (
              <SimpleGrid columns={[1, 2, 3]} spacing={3}>
                {woundEntries.map(([woundIndex, grafts]) => {
                  const idxNum = parseInt(woundIndex, 10);
                  const meta = getWoundMeta(order, idxNum, grafts[0]);
                  const dims = formatWoundDimensions(meta);

                  return (
                    <Box
                      key={woundIndex}
                      borderWidth="1px"
                      borderRadius="md"
                      bg="white"
                      p={3}
                      boxShadow="sm"
                    >
                      <Text fontSize="sm" fontWeight="semibold">
                        Wound {idxNum + 1}: {meta.type || "—"}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        {dims}
                      </Text>
                      <VStack align="stretch" spacing={1} mt={2}>
                        {grafts.map((g, i) => (
                          <HStack key={i} justify="space-between" align="center">
                            <Text fontSize="xs" color="gray.700">
                              <strong>{g.product}</strong>{" "}
                              ({g.qCode || g.productId || "—"})
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              {g.size} • Qty: {g.quantity}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  );
                })}
              </SimpleGrid>
            ) : (
              <Box borderWidth="1px" borderRadius="md" bg="white" p={3} boxShadow="sm">
                {(() => {
                  const meta = getWoundMeta(order, 0, order.graftsUsed[0] || {});
                  const dims = formatWoundDimensions(meta);
                  return (
                    <>
                      <Text fontSize="sm" fontWeight="semibold">
                        Wound: {meta.type || "—"}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        {dims}
                      </Text>
                    </>
                  );
                })()}
                <VStack align="stretch" spacing={1} mt={2}>
                  {order.graftsUsed.map((g, i) => (
                    <HStack key={i} justify="space-between" align="center">
                      <Text fontSize="xs" color="gray.700">
                        <strong>{g.product}</strong>{" "}
                        ({g.qCode || g.productId || "—"})
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        {g.size} • Qty: {g.quantity}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )
          ) : (
            <Text fontSize="sm" color="gray.500">
              No grafts listed.
            </Text>
          )}
        </Box>
      </Box>
    );
  };


  /* ---------------------------
     Actions / handlers
  ---------------------------*/
  const handleCreateInvoiceClick = async (order) => {
    const grafts = order.graftsUsed || [];
    const token = getIdToken();
    const allProducts = await getProductListClean(token, { um: true, strong: true });

    const items = await Promise.all(
      grafts.map(async (graft) => {
        let matchedProduct = null;
        if (graft.qCode) {
          try {
            matchedProduct = await getProductByQCode(graft.qCode);
          } catch {
            // ignore, fallback below
          }
        }
        if (!matchedProduct) {
          matchedProduct = allProducts.find(
            (p) =>
              p.productName?.toLowerCase() === graft.productId?.toLowerCase() ||
              p.productId?.toLowerCase() === graft.productId?.toLowerCase()
          );
        }
        if (!matchedProduct) return null;
        console.log("Matched product for invoice:", matchedProduct, "for graft:", graft);

        return {
          name: matchedProduct.name || matchedProduct.productName,
          quantity: graft.quantity || 1,
          price: matchedProduct.price || 0,
          total: (graft.quantity || 1) * (matchedProduct.price || 0),
          qCode: matchedProduct.qCode,
          size: graft.size,
        };
      })
    );

    const filtered = items.filter(Boolean);
    setInvoicePrefillData({
      userId: order.userId,
      orderId: order.orderId,
      facilityId: order.facilityId || undefined,
      lineItems: filtered.map((item) => ({
        label: `${item.name} (${item.qCode || "no Q"}) - ${item.size}`,
        quantity: item.quantity,
        unitPrice: item.price,
      })),
    });
    setCreateInvoiceOpen(true);
  };

  // REPLACE the entire handleGeneratePackingList with this:
  const handleGeneratePackingList = async (
    order,
    {
      mode = "auto", // "auto" | "create" | "edit" | "regenerate"
      graftsWithSku = null,
      skus = [],
      useShippingOverride = false,
      shippingAddressOverride = null,
      skipPdfView = false, // NEW: skip opening PDF (for bulk operations)
    } = {}
  ) => {
    setPackingInProgress((prev) => ({ ...prev, [order.orderId]: true }));

    const token = getIdToken();
    const graftsToSend = graftsWithSku || order.graftsUsed || [];
    const isEdit = mode === "edit" || (mode === "auto" && Boolean(order?.packingId));
    const isRegenerate = mode === "regenerate";

    try {
      const body = isEdit
        ? {
            // ✏️ EDIT
            packingId: order.packingId,
            trackingNumber: order.trackingNumber || "",
            carrierName: order.carrier || "",
            graftsUsed: graftsToSend,
            skus,
            useShippingOverride,
            shippingAddressOverride, // null if toggle off
          }
        : {
            // 🆕 CREATE (or REGENERATE when order already has packingId)
            orderId: order.orderId,
            invoiceId: order.invoiceId || null, // Ensure null instead of undefined
            trackingNumber: order.trackingNumber || "",
            carrierName: order.carrier || "",
            graftsUsed: graftsToSend,
            skus,
            regenerate: isRegenerate, // only matters if an older packingId exists
            useShippingOverride,
            shippingAddressOverride, // null if toggle off
          };

      const res = await fetch(
        "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/generateMedicalPackingList",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const data = await res.json();
      if (res.ok) {
        toast({
          title: isEdit ? "Packing List Updated!" : "Packing List Created!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // refresh + keep focus
        const anchor = {
          facilityName: order.facilityName,
          patientKey: buildPatientKeyFromOrder(order),
          orderId: order.orderId,
        };
        await refreshAndRefocus(anchor);

        // Only view PDF if not in bulk mode or skipPdfView is false
        if (data.packingId && !skipPdfView) {
          await handleViewPackingList(data.packingId);
        }
      } else {
        console.error("Packing list API error:", { 
          status: res.status, 
          data, 
          body: isEdit ? { packingId: order.packingId } : { orderId: order.orderId, invoiceId: order.invoiceId }
        });
        toast({
          title: "Error",
          description: data.message || "Failed to generate packing list.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Packing list error:", err);
      toast({
        title: "Unexpected Error",
        description: "Failed to create/update packing list.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPackingInProgress((prev) => ({ ...prev, [order.orderId]: false }));
    }
  };

  const handleViewPackingList = async (packingId) => {
    const token = getIdToken();
    const res = await fetch("https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getPackingList", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ packingId }),
    });
    const data = await res.json();
    if (res.ok && (data.packingList?.signedPdfUrl || data.packingList?.pdfUrl)) {
      setPackingPdfUrl(data.packingList.signedPdfUrl || data.packingList.pdfUrl);
      setViewPackingModalOpen(true);
    } else {
      toast({ title: "Failed to fetch packing list", status: "error" });
    }
  };

  const toggleSection = (facilityName) => {
    setOpenSections((prev) => ({
      ...prev,
      [facilityName]: !prev[facilityName],
    }));
  };

  const togglePatient = (facilityName, patientKey) => {
    setOpenPatients((prev) => ({
      ...prev,
      [facilityName]: {
        ...(prev[facilityName] || {}),
        [patientKey]: !prev[facilityName]?.[patientKey],
      },
    }));
  };

  const handleRegeneratePdf = async () => {
    if (!selectedOrder) return;
    setPdfRegenerationInProgress(true);

    try {
      const token = getIdToken();
      const response = await fetch(
        "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/regenerateOrderPdf",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            orderId: selectedOrder.orderId,
            userId: selectedOrder.userId,
            mode: pdfGenerationMode,
            sendDraft: !!sendDraftOrderEmail,
          }),
        }
      );

      const result = await response.json();
      if (result.success) {
        toast({
          title: "PDF Regenerated Successfully",
          description: `Order PDF regenerated with ${pdfGenerationMode} formatting`,
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        setRegeneratePdfModalOpen(false);

        const anchor = {
          facilityName: selectedOrder.facilityName,
          patientKey: buildPatientKeyFromOrder(selectedOrder),
          orderId: selectedOrder.orderId,
        };
        await refreshAndRefocus(anchor);
      } else {
        throw new Error(result.error || "PDF regeneration failed");
      }
    } catch (err) {
      console.error("PDF regeneration error:", err);
      toast({
        title: "PDF Regeneration Failed",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPdfRegenerationInProgress(false);
    }
  };

  const handleEmailOrderPdf = (order) => {
    setSelectedOrder(order);
    setEmailOrderModalOpen(true);
  };

  /* ---------------------------
     Bulk bar handlers
  ---------------------------*/
  const handleCompletePackingSlips = useCallback(() => {
    const missing = selectedOrders.filter((o) => !o.packingId);
    if (!missing.length) {
      toast({
        title: "All selected orders already have packing slips",
        status: "info",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    // Initialize bulk mode
    setBulkPackingQueue([...missing]);
    setIsBulkPackingMode(true);
    
    // Start with the first missing order
    const firstMissing = missing[0];
    const found = findOrderAnchor(firstMissing.orderId);
    if (found) {
      openAndScrollToFoundOrder(found);
    }

    setPendingPackingOrder(firstMissing);
    setPackingInputModalOpen(true);
  }, [selectedOrders, findOrderAnchor, openAndScrollToFoundOrder, toast]);

  // Process next order in bulk queue
  const processNextBulkOrder = useCallback(() => {
    if (bulkPackingQueue.length <= 1) {
      // Queue is empty or this was the last one - move to combine step
      setBulkPackingQueue([]);
      setIsBulkPackingMode(false);
      setPackingInputModalOpen(false);
      setPendingPackingOrder(null);
      
      // Reset tracking fields for fresh start
      setSharedTrackingNumber("");
      setSharedCarrier("FedEx");
      
      // Open combine modal
      setCombineModalOpen(true);
      return;
    }

    // Remove current order and set next one
    const nextQueue = bulkPackingQueue.slice(1);
    setBulkPackingQueue(nextQueue);
    
    const nextOrder = nextQueue[0];
    const found = findOrderAnchor(nextOrder.orderId);
    if (found) {
      openAndScrollToFoundOrder(found);
    }
    
    setPendingPackingOrder(nextOrder);
    // Keep modal open for next order
  }, [bulkPackingQueue, findOrderAnchor, openAndScrollToFoundOrder]);

  const handleBulkCombinePackingSlips = useCallback(async () => {
    if (!bulkStats.canCombine || !selectedOrders.length) return;

    setCombineInProgress(true);
    
    // 📦 Everything we know about this batch
    const packingIds = selectedOrders
      .filter((o) => o.packingId)
      .map((o) => o.packingId);

    const orderIds = selectedOrders.map((o) => o.orderId);
    const invoiceIds = selectedOrders
      .filter((o) => o.invoiceId)
      .map((o) => o.invoiceId);

    try {
      const token = getIdToken();

      const res = await fetch(COMBINE_PACKING_API, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          packingIds,
          orderIds,
          invoiceIds,
          carrierName: sharedCarrier || null,
          trackingNumber: sharedTrackingNumber || null,
          saveCombined: true,         // be explicit that we want it persisted
          test: isTestMode || false,  // pass through test flag if in test mode
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to combine packing slips");
      }

      // ✅ Prefer signed URL (no more AccessDenied)
      const combinedUrl = data.signedPdfUrl || data.pdfUrl || data.url;
      if (combinedUrl) {
        window.open(combinedUrl, "_blank");
      }

      toast({
        title: "Combined packing slips generated",
        description: `Created combined packing list ${data.packingId || ""} for ${packingIds.length} orders`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      setCombineModalOpen(false);
      clearOrderSelection();
    } catch (err) {
      console.error("Bulk combine error:", err);
      toast({
        title: "Combine failed",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCombineInProgress(false);
    }
  }, [
    bulkStats.canCombine,
    selectedOrders,
    sharedTrackingNumber,
    sharedCarrier,
    getIdToken,
    isTestMode,
    toast,
    clearOrderSelection,
  ]);

  /* ---------------------------
     UI
  ---------------------------*/
  if (loading) {
    return (
      <Box p={8}>
        <Heading size="lg" mb={4}>
          Admin Order Management
        </Heading>
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box p={[4, 6, 8]}>
      <Heading size="lg" mb={2}>
        Admin Order Management
      </Heading>

      {/* 💡 Quick Stats */}
      <SimpleGrid columns={[1, 2, 4]} spacing={4} my={4}>
        <Card boxShadow="sm" bg="blue.50">
          <CardBody>
            <Stat>
              <StatLabel>Total Orders</StatLabel>
              <StatNumber color="blue.600">{stats.total}</StatNumber>
              <StatHelpText>All time</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card boxShadow="sm" bg="orange.50">
          <CardBody>
            <Stat>
              <StatLabel>In Progress</StatLabel>
              <StatNumber color="orange.600">{stats.inProgress}</StatNumber>
              <StatHelpText>Working queue</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card boxShadow="sm" bg="green.50">
          <CardBody>
            <Stat>
              <StatLabel>Completed</StatLabel>
              <StatNumber color="green.600">{stats.completed}</StatNumber>
              <StatHelpText>Shipped / Fulfilled</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card boxShadow="sm" bg="yellow.50">
          <CardBody>
            <Stat>
              <StatLabel>Pending Action</StatLabel>
              <StatNumber color="yellow.700">{stats.pendingAction}</StatNumber>
              <StatHelpText>Needs attention</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* 🔎 Search / Jump */}
      <HStack spacing={2} mb={4} align="center">
        <Input
          size="sm"
          maxW="420px"
          placeholder="Search Order ID, IVR ID, patient, facility, user, or email…"
          value={searchOrderId}
          onChange={(e) => setSearchOrderId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleJumpToOrder();
          }}
        />
        <Button size="sm" onClick={handleJumpToOrder} _hover={{ transform: "scale(1)" }}>
          Go
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setSearchOrderId("");
            clearHighlight();
          }}
          _hover={{ transform: "scale(1)" }}
        >
          Clear
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={async () => {
            await refreshAndRefocus(focusAnchorRef.current);
            toast({ title: "Orders refreshed", status: "success", duration: 1000 });
          }}
          _hover={{ transform: "scale(1)" }}
        >
          Refresh (Alt+R)
        </Button>
      </HStack>

      {isRefreshing && (
        <HStack spacing={2} mb={4}>
          <Spinner size="sm" />
          <Text fontSize="sm" color="gray.600">
            Refreshing data…
          </Text>
        </HStack>
      )}

      <Box mb={6}>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<QuestionIcon />}
          onClick={() => setShowHelpBox(!showHelpBox)}
          mb={showHelpBox ? 3 : 0}
          _hover={{ transform: "scale(1)" }}
        >
          {showHelpBox ? "Hide" : "Show"} Order Fulfillment Process
        </Button>

        {showHelpBox && (
          <Box p={4} bg="yellow.50" borderLeft="6px solid" borderColor="yellow.400" borderRadius="md" boxShadow="sm">
            <Text fontSize="md" fontWeight="medium" color="gray.800" mb={1} display="flex" alignItems="center" gap={2}>
              Order Fulfillment Process
            </Text>
            <Text fontSize="sm" color="gray.700">
              <strong> Step 1:</strong> <u>Update Tracking Info</u> → <strong> Step 2:</strong> <u>Create Packing List</u> →
              <strong> Step 3:</strong> <u>Print Packing List</u> → <strong> Step 4:</strong> <u>Ship Order</u>
            </Text>
          </Box>
        )}
      </Box>

      {/* Tabbed Views */}
      <Tabs variant="enclosed" colorScheme="teal">
        <TabList>
          <Tab>By Facility</Tab>
          <Tab>Chronological</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            {Object.entries(enrichedOrdersByFacility).map(([facilityName, patientsObj]) => {
              const isOpen = !!openSections[facilityName];
              const firstPatient = patientsObj && Object.values(patientsObj)[0];
              const npi = firstPatient?.orders?.[0]?.facilityNpi || "—";
              const totalOrders = Object.values(patientsObj).reduce(
                (sum, patientBucket) => sum + (patientBucket.orders?.length || 0),
                0
              );

              return (
                <Box key={facilityName} mb={6} borderWidth="1px" borderRadius="xl" overflow="hidden" boxShadow="md">
                  {/* Facility header */}
                  <Box
                    p={FACILITY_HEADER_P}
                    bg="gray.100"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    cursor="pointer"
                    _hover={{ bg: "gray.200" }}
                    onClick={() => toggleSection(facilityName)}
                  >
                    <Box>
                      <Heading size="sm">{facilityName}</Heading>
                      <Text fontSize="sm" color="gray.600">
                        NPI: {npi} • {Object.keys(patientsObj).length} patient
                        {Object.keys(patientsObj).length > 1 ? "s" : ""} • {totalOrders} order{totalOrders > 1 ? "s" : ""}
                      </Text>
                    </Box>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSection(facilityName);
                      }}
                      _hover={{ transform: "scale(1)" }}
                    >
                      {isOpen ? "Collapse" : "Expand"}
                    </Button>
                  </Box>

                  {isOpen && (
                    <Box p={4}>
                      {Object.entries(patientsObj).map(([patientKey, patientBucket]) => {
                        const pOpen = !!openPatients[facilityName]?.[patientKey];
                        const orders = patientBucket.orders || [];

                        return (
                          <Box key={patientKey} mb={2} borderWidth="1px" borderRadius="md" overflow="hidden">
                            {/* Patient header */}
                            <Box
                              p={PATIENT_HEADER_P}
                              bg="white"
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              cursor="pointer"
                              _hover={{ bg: "gray.50" }}
                              onClick={() => togglePatient(facilityName, patientKey)}
                            >
                              <Box>
                                <Text fontWeight="semibold">{patientBucket.patientName}</Text>
                                <Text fontSize="sm" color="gray.600">
                                  {[
                                    patientBucket.patientMBI && `MBI: ${patientBucket.patientMBI}`,
                                    patientBucket.patientSSN && `SSN: ${patientBucket.patientSSN}`,
                                    `${orders.length} order${orders.length > 1 ? "s" : ""}`,
                                  ]
                                    .filter(Boolean)
                                    .join(" • ")}
                                </Text>
                              </Box>
                              <Button
                                size="xs"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  togglePatient(facilityName, patientKey);
                                }}
                                _hover={{ transform: "scale(1)" }}
                              >
                                {pOpen ? "Hide Orders" : "View Orders"}
                              </Button>
                            </Box>

                            {/* Orders for patient (shared row layout) */}
                            {pOpen && (
                              <Box p={3} bg="gray.50">
                                <VStack align="stretch" spacing={3}>
                                  {orders.map((order) =>
                                    renderOrderRow(order, {
                                      showFacility: false,          // facility already in header
                                      showShippingAddress: true,    // show ship-to in this view
                                    })
                                  )}
                                </VStack>
                              </Box>
                            )}

                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              );
            })}
          </TabPanel>

          <TabPanel>{renderChronologicalView()}</TabPanel>
        </TabPanels>
      </Tabs>

      {/* Modals */}
      {shippingModalOpen && selectedOrder && (
        <UpdateShippingModal
          isOpen={shippingModalOpen}
          onClose={() => setShippingModalOpen(false)}
          orderId={selectedOrder.orderId}
          currentCarrier={selectedOrder.carrier}
          currentTracking={selectedOrder.trackingNumber}
          onSuccess={async () => {
            setShippingModalOpen(false);
            const anchor = {
              facilityName: selectedOrder.facilityName,
              patientKey: buildPatientKeyFromOrder(selectedOrder),
              orderId: selectedOrder.orderId,
            };
            await refreshAndRefocus(anchor);
          }}
        />
      )}

      {createInvoiceOpen && (
        <CreateInvoiceModal
          isOpen={createInvoiceOpen}
          onClose={() => setCreateInvoiceOpen(false)}
          initialData={invoicePrefillData}
          onSuccess={async () => {
            setCreateInvoiceOpen(false);
            const anchor = {
              facilityName: invoicePrefillData.facilityName || selectedOrder?.facilityName || "",
              patientKey: selectedOrder
                ? buildPatientKeyFromOrder(selectedOrder)
                : buildPatientKeyFromOrder({ patientName: "", patientMBI: "", patientSSN: "" }),
              orderId: invoicePrefillData.orderId,
            };
            const freshOrder = await refreshAndRefocus(anchor);
            if (freshOrder?.invoiceId) {
              setSelectedInvoiceId(freshOrder.invoiceId);
              setSelectedOrder(freshOrder);
              setViewInvoiceOpen(true);
            }
          }}
        />
      )}

      {viewInvoiceOpen && selectedInvoiceId && (
        <ViewInvoiceModal
          isOpen={viewInvoiceOpen}
          onClose={() => setViewInvoiceOpen(false)}
          invoiceId={selectedInvoiceId}
          facilityName={selectedOrder?.facilityName}
        />
      )}

      {viewPackingModalOpen && packingPdfUrl && (
        <ViewPdfModal
          isOpen={viewPackingModalOpen}
          onClose={() => {
            setViewPackingModalOpen(false);
            setPackingPdfUrl(null);
          }}
          pdfUrl={packingPdfUrl}
        />
      )}

      {regeneratePdfModalOpen && selectedOrder && (
        <Modal isOpen={regeneratePdfModalOpen} onClose={() => setRegeneratePdfModalOpen(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Regenerate Order PDF</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={4}>
                Regenerate PDF for Order: <strong>{selectedOrder.orderId}</strong>
              </Text>
              <HStack spacing={4} mb={3}>
                <Text fontSize="sm" fontWeight="medium">
                  PDF Format:
                </Text>
                <HStack>
                  <Text fontSize="sm">Physician Facility</Text>
                  <Switch
                    isChecked={pdfGenerationMode === "curagenesis"}
                    onChange={(e) => setPdfGenerationMode(e.target.checked ? "curagenesis" : "facility")}
                    colorScheme="blue"
                  />
                  <Text fontSize="sm">CuraGenesis Office</Text>
                </HStack>
              </HStack>
              <HStack spacing={4} mb={3}>
                <Text fontSize="sm" fontWeight="medium">
                  Send Draft of Order EMail?
                </Text>
                <Switch
                  isChecked={sendDraftOrderEmail}
                  onChange={(e) => setSendDraftOrderEmail(e.target.checked)}
                  colorScheme="blue"
                />
              </HStack>
              <Text fontSize="sm" color="gray.600">
                Choose the formatting style for the regenerated PDF document.
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="ghost"
                mr={3}
                onClick={() => setRegeneratePdfModalOpen(false)}
                isDisabled={pdfRegenerationInProgress}
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleRegeneratePdf}
                isLoading={pdfRegenerationInProgress}
                loadingText="Regenerating..."
                _hover={{ transform: "scale(1)" }}
              >
                Regenerate PDF ({pdfGenerationMode})
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {emailOrderModalOpen && selectedOrder && (
        <EmailOrderPdfModal
          isOpen={emailOrderModalOpen}
          onClose={() => setEmailOrderModalOpen(false)}
          order={selectedOrder}
          isTestMode={isTestMode}
        />
      )}

      {packingInputModalOpen && pendingPackingOrder && (
        <PackingListInputModal
          isOpen={packingInputModalOpen}
          onClose={() => {
            // If closing during bulk mode, reset bulk state
            if (isBulkPackingMode) {
              setBulkPackingQueue([]);
              setIsBulkPackingMode(false);
            }
            setPackingInputModalOpen(false);
          }}
          order={pendingPackingOrder}
          getAuthToken={getIdToken}
          isSubmitting={packingSubmissionInProgress}
          // Bulk mode props
          isBulkMode={isBulkPackingMode}
          currentStep={isBulkPackingMode ? (selectedOrders.filter(o => !o.packingId).length - bulkPackingQueue.length + 1) : null}
          totalSteps={isBulkPackingMode ? selectedOrders.filter(o => !o.packingId).length : null}
          onSubmit={async ({
            carrier,
            trackingNumber,
            graftsWithSku,
            skus,
            useShippingOverride,
            shippingAddressOverride,
          }) => {
            setPackingSubmissionInProgress(true);
            try {
              const isEdit = Boolean(pendingPackingOrder?.packingId);
              const updatedOrder = {
                ...pendingPackingOrder,
                carrier,
                trackingNumber,
              };

              await handleGeneratePackingList(updatedOrder, {
                mode: isEdit ? "edit" : "create",
                graftsWithSku,
                skus,
                useShippingOverride,
                shippingAddressOverride,
                skipPdfView: isBulkPackingMode, // Skip PDF during bulk operations
              });

              // Handle bulk mode progression
              if (isBulkPackingMode) {
                processNextBulkOrder();
              } else {
                setPackingInputModalOpen(false);
              }
            } catch (error) {
              console.error("Packing list create/update failed:", error);
            } finally {
              setPackingSubmissionInProgress(false);
            }
          }}
        />
      )}

      {/* Combine Packing Slips Modal */}
      {combineModalOpen && (
        <Modal isOpen={combineModalOpen} onClose={() => setCombineModalOpen(false)} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Create Combined Shipment</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text mb={2}>
                    Ready to combine packing slips for <strong>{selectedOrders.length}</strong> orders into a single PDF.
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    This creates one combined shipment with all orders going to their respective addresses 
                    but under one shared tracking number (if provided).
                  </Text>
                </Box>
                
                <Box p={4} bg="gray.50" borderRadius="md">
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    Orders to combine:
                  </Text>
                  <VStack spacing={1} align="start">
                    {selectedOrders.map((order) => (
                      <Text key={order.orderId} fontSize="sm">
                        • {order.orderId} - {order.patientName} ({order.facilityName})
                      </Text>
                    ))}
                  </VStack>
                </Box>

                <FormControl>
                  <FormLabel>Shipment Carrier (optional)</FormLabel>
                  <Select 
                    value={sharedCarrier} 
                    onChange={(e) => setSharedCarrier(e.target.value)}
                    placeholder="Select carrier for this shipment"
                  >
                    <option value="FedEx">FedEx</option>
                    <option value="UPS">UPS</option>
                    <option value="USPS">USPS</option>
                    <option value="DHL">DHL</option>
                  </Select>
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    The carrier for this combined shipment
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Shipment Tracking Number (optional)</FormLabel>
                  <Input
                    value={sharedTrackingNumber}
                    onChange={(e) => setSharedTrackingNumber(e.target.value)}
                    placeholder="Enter tracking number for this combined shipment"
                  />
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    <strong>One tracking number</strong> for the entire combined shipment. 
                    This will appear on all packing slips in the PDF.
                  </Text>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button 
                variant="ghost" 
                mr={3} 
                onClick={() => setCombineModalOpen(false)}
                isDisabled={combineInProgress}
              >
                Cancel
              </Button>
              <Button 
                colorScheme="purple" 
                onClick={handleBulkCombinePackingSlips}
                isLoading={combineInProgress}
                loadingText="Generating..."
                _hover={{ transform: "scale(1)" }}
              >
                Generate Combined PDF
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* 🔽 Sticky bulk action bar */}
      {bulkStats.totalSelected > 0 && (
        <OrderBulkActionBar
          totalSelected={bulkStats.totalSelected}
          packingCompleteCount={bulkStats.packingCompleteCount}
          packingMissingCount={bulkStats.packingMissingCount}
          trackingCompleteCount={bulkStats.trackingCompleteCount}
          trackingMissingCount={bulkStats.trackingMissingCount}
          requireTrackingForCombine={requireTrackingForCombine}
          onToggleRequireTracking={setRequireTrackingForCombine}
          onClearSelection={clearOrderSelection}
          onCompletePackingSlips={handleCompletePackingSlips}
          onCombinePackingSlips={handleBulkCombinePackingSlips}
          canCombine={bulkStats.canCombine}
          combineInProgress={combineInProgress}
        />
      )}
    </Box>
  );
};

export default OrdersPage;
